import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Navigation } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Reveal";
import { LuxButton } from "@/components/LuxButton";
import { riverStone, IMG } from "@/data/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const times = ["5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM", "8:30 PM", "9:00 PM"];
const inputCls = "w-full bg-transparent border-b border-white/20 py-3.5 text-white placeholder:text-white/30 text-sm font-light tracking-wide focus:outline-none focus:border-[#c5a059] transition-colors [color-scheme:dark]";

const ReservationsPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "7:00 PM", party_size: "2", notes: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.date) {
      toast.error("Please complete name, email, phone and date.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, { ...form, brand: "river-stone" });
      toast.success("Reservation Demonstration — in production, this would continue securely to River Stone's reservation provider (OpenTable).", { duration: 6000 });
      setForm({ name: "", email: "", phone: "", date: "", time: "7:00 PM", party_size: "2", notes: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="reservations-page">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden bg-black pt-24">
        <motion.img src={IMG.reservationTable} alt="Reserve" className="absolute inset-0 w-full h-full object-cover" initial={{ scale: 1.2 }} animate={{ scale: 1 }} transition={{ duration: 1.8 }} />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 text-center px-6 py-16">
          <p className="text-[10px] tracking-[0.45em] uppercase text-[#c5a059] mb-5">Reservations</p>
          <h1 className="font-serif text-white font-light text-5xl md:text-7xl leading-none">
            <MaskLines lines={["Reserve a Table"]} delay={0.3} />
          </h1>
        </div>
      </section>

      <section className="bg-[#0a0908] py-20 md:py-28 grain relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16">
          {/* Booking form */}
          <Reveal>
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">Book Online</span>
            <h2 className="font-serif text-4xl md:text-5xl text-white font-light mt-4">Your details</h2>
            <form onSubmit={submit} data-testid="reservation-form" className="mt-10 space-y-6">
              <div className="grid sm:grid-cols-3 gap-6">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-white/40">Date</label>
                  <input data-testid="res-date" aria-label="Date" type="date" className={inputCls} value={form.date} onChange={set("date")} />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-white/40">Time</label>
                  <select data-testid="res-time" aria-label="Time" className={`${inputCls} appearance-none cursor-pointer`} value={form.time} onChange={set("time")}>
                    {times.map((t) => <option key={t} className="bg-[#161412]">{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-white/40">Party</label>
                  <select data-testid="res-party" aria-label="Party size" className={`${inputCls} appearance-none cursor-pointer`} value={form.party_size} onChange={set("party_size")}>
                    {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <option key={n} value={n} className="bg-[#161412]">{n} {n === 1 ? "guest" : "guests"}</option>)}
                  </select>
                </div>
              </div>
              <input data-testid="res-name" aria-label="Full Name" className={inputCls} placeholder="Full Name" value={form.name} onChange={set("name")} />
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="res-email" aria-label="Email" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} />
                <input data-testid="res-phone" aria-label="Phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
              </div>
              <input data-testid="res-notes" aria-label="Special requests (optional)" className={inputCls} placeholder="Special requests (optional)" value={form.notes} onChange={set("notes")} />
              <div className="pt-2">
                <LuxButton type="submit" variant="gold" testid="res-submit">{loading ? "Continuing…" : "Reserve with OpenTable"}</LuxButton>
              </div>
              <p className="text-[11px] text-white/30 font-light tracking-wide pt-2">
                Concept demonstration. In production, this action would continue securely to River Stone&rsquo;s reservation provider (OpenTable). No booking is confirmed by River Stone here.
              </p>
            </form>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.15} id="contact">
            <div className="border border-white/10 p-8 md:p-10 bg-[#161412]">
              <h3 className="font-serif text-3xl text-white mb-8">Visit River Stone</h3>
              <div className="space-y-7">
                <div className="flex gap-4">
                  <MapPin className="text-[#c5a059] shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Address</p>
                    <p className="text-white font-light">{riverStone.location.address}<br />{riverStone.location.cityStateZip}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Phone className="text-[#c5a059] shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Phone</p>
                    <a href={riverStone.phoneHref} className="text-white font-light hover:text-[#c5a059] transition-colors">{riverStone.phone}</a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Clock className="text-[#c5a059] shrink-0 mt-1" size={20} />
                  <div className="flex-1">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-2">Hours</p>
                    {riverStone.hours.map((h) => (
                      <div key={h.day} className="flex justify-between text-sm text-white/70 font-light py-0.5">
                        <span>{h.day}</span><span className="text-white/50">{h.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <a href={riverStone.location.mapUrl} target="_blank" rel="noreferrer" data-testid="res-directions" className="mt-8 inline-flex items-center gap-3 text-[#c5a059] text-xs tracking-[0.2em] uppercase border-b border-[#c5a059]/40 pb-1.5 hover:gap-5 transition-all">
                <Navigation size={15} /> Get Directions
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default ReservationsPage;
